import { gql, useMutation } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import { Select } from '@apollo/space-kit/Select';
import { useFormik } from 'formik';
import { capitalize } from 'lodash';
import React, { useCallback, useState } from 'react';
import * as Yup from 'yup';

import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { assertUnreachableOrReturnDefault } from 'src/lib/assertUnreachable';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { ProposalStatus } from 'src/lib/graphqlTypes/types';

const validationSchema = Yup.object().shape({
  status: Yup.mixed<ProposalStatus>()
    .oneOf(Object.values(ProposalStatus))
    .required(),
});

type FormValues = Yup.InferType<typeof validationSchema>;

const getSelectStatusName = (status: ProposalStatus) => {
  switch (status) {
    case ProposalStatus.OPEN:
      return 'Open for feedback';
    case ProposalStatus.APPROVED:
    case ProposalStatus.DRAFT:
    case ProposalStatus.CLOSED:
    case ProposalStatus.IMPLEMENTED:
      return capitalize(status);
    default:
      assertUnreachableOrReturnDefault(status, capitalize(status));
  }
};

export const ChangeProposalStatusModal = ({
  proposalId,
  isOpen,
  onClose,
  currentStatus,
}: {
  proposalId: string;
  isOpen: boolean;
  onClose: () => void;
  currentStatus: ProposalStatus;
}) => {
  const [errorMessage, setErrorMessage] = useState('');
  const [changeStatus] = useMutation<
    GraphQLTypes.ChangeProposalStatusMutation,
    GraphQLTypes.ChangeProposalStatusMutationVariables
  >(gql`
    mutation ChangeProposalStatusMutation(
      $proposalId: ID!
      $status: ProposalStatus!
    ) {
      proposal(id: $proposalId) {
        ... on ProposalMutation {
          updateStatus(status: $status) {
            ... on ValidationError {
              message
            }
            ... on Proposal {
              id
              status
            }
          }
        }
      }
    }
  `);

  const {
    values,
    errors,
    touched,
    isSubmitting,
    handleBlur,
    handleChange,
    handleSubmit,
    resetForm,
    isValid,
  } = useFormik<FormValues>({
    enableReinitialize: true,
    validateOnBlur: true,
    validateOnChange: true,
    initialValues: {
      status: currentStatus,
    },
    onSubmit: async ({ status }) => {
      const { data: responseData } = await changeStatus({
        variables: {
          proposalId,
          status,
        },
        update: (cache) => {
          cache.evict({
            id: cache.identify({
              __typename: 'Proposal',
              id: proposalId,
            }),
            fieldName: 'status',
          });
        },
      });

      if (
        responseData?.proposal.__typename === 'ProposalMutation' &&
        responseData.proposal.updateStatus.__typename === 'Proposal'
      ) {
        onClose();
        resetForm({});
        setErrorMessage('');
      } else if (
        responseData?.proposal.__typename === 'NotFoundError' ||
        responseData?.proposal.__typename === 'ValidationError' ||
        responseData?.proposal.__typename === 'PermissionError' ||
        responseData?.proposal.updateStatus.__typename === 'ValidationError' ||
        !responseData?.proposal.updateStatus
      ) {
        setErrorMessage('An error has occurred');
      } else if (!responseData.proposal.updateStatus) {
        setErrorMessage(
          assertUnreachableOrReturnDefault(
            responseData.proposal.updateStatus,
            'An error has occurred',
          ),
        );
      }
    },
    validationSchema,
  });

  const onCloseModal = useCallback(() => {
    onClose();
    resetForm({});
    setErrorMessage('');
  }, [onClose, resetForm]);

  return (
    <Modal size="lg" isOpen={isOpen} onClose={onCloseModal}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Change the status of your proposal</ModalTitle>
        </ModalHeader>
        <ModalBody>
          {!!errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
          <FormControl isInvalid={!!errors.status && !!touched.status}>
            <FormLabel>Status</FormLabel>
            <Select
              name="status"
              onBlur={handleBlur}
              onChange={handleChange}
              value={values.status}
              className="w-full"
              // Note: The width of the popper component only is meant to fit
              // the content of its values, however it is standard for select
              // components dropdowns to match the width of the trigger node.
              // This modifier below is an adapted version of the community sameWidth
              // modifier from popper.
              // source: https://codesandbox.io/s/bitter-sky-pe3z9?file=/src/index.js
              popperOptions={{
                modifiers: [
                  {
                    name: 'sameWidth',
                    enabled: true,
                    phase: 'beforeWrite',
                    requires: ['computeStyles'],
                    fn: ({ state }) => {
                      if (state.styles.popper) {
                        // eslint-disable-next-line no-param-reassign
                        state.styles.popper.width = `${state.rects.reference.width}px`;
                      }
                    },
                    effect: ({ state }) => {
                      // eslint-disable-next-line no-param-reassign
                      state.elements.popper.style.width = `${
                        state.elements.reference.getBoundingClientRect().width
                      }px`;
                      // eslint-disable-next-line no-param-reassign
                      state.elements.popper.className =
                        '[&>.tippy-box]:!max-w-full';
                    },
                  },
                ],
              }}
              renderTriggerNode={() => (
                <span className="font-normal text-secondary">
                  {values.status
                    ? getSelectStatusName(values.status)
                    : 'Select a Status'}
                </span>
              )}
            >
              {Object.values(ProposalStatus)
                // Approved status is controlled by the backend so we
                // should filter it from this list
                .filter(
                  (statusName) =>
                    statusName !== ProposalStatus.APPROVED &&
                    statusName !== ProposalStatus.IMPLEMENTED,
                )
                .map((statusName) => (
                  <option value={statusName} key={statusName}>
                    {getSelectStatusName(statusName)}
                  </option>
                ))}
            </Select>
            {/* TODO (proposals-notifications): add helper text with notification details here */}
            <FormErrorMessage>{errors.status}</FormErrorMessage>
            {values.status === ProposalStatus.CLOSED && (
              <FormHelperText iconType="info">
                Proposals can be reopened at any time. Reviewers will be
                notified when proposals are closed or reopened.
              </FormHelperText>
            )}
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup>
            <Button variant="secondary" onClick={onCloseModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              isDisabled={touched.status && !isValid}
              isLoading={isSubmitting}
              loadingText="Editing"
              type="submit"
              onClick={() => handleSubmit()}
            >
              Edit Status
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
